import { CurrencyCode } from "#graphql/supergraphEnums";
import { type MoneyType } from "../../Money/types";
import { type CashBalance } from "./types";

const usd = (amount: number): MoneyType => ({
  amount,
  currency: CurrencyCode.Usd,
  precision: 2,
});

/** Shared "as of" date for the fixture rows. */
const AS_OF = "2026-07-02";

/**
 * Default fixture for `useCashAccountBalances` (M1 stub) and tests/stories. Colocated with the component (not under `__tests__`)
 * since the stub datasource imports them and ships in the built package.
 *
 * Shape exercised:
 *  - Collection "Evergreen Fund Family" grouping two entities (Evergreen I, II),
 *    each with accounts at multiple banks.
 *  - A collection-less entity ("Standalone Holdings LP", `collectionName: null`)
 *    so `groupAllowUnbalanced` and the "Entities" section are covered.
 *  - Multiple banks (First National, Summit Bank) and multiple accounts per entity.
 */
export const cashAccountBalancesFixture: CashBalance[] = [
  {
    bankAccountId: "acct-3",
    bankAccountLast4: "2001",
    bankName: "First National",
    entityId: "entity-evg-2",
    entityName: "Evergreen Fund II",
    collectionId: "col-evg",
    collectionName: "Evergreen Fund Family",
    balance: usd(310_000_00),
    delta: usd(22_000_00),
    deltaPercent: 7.64,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-4",
    bankAccountLast4: "3001",
    bankName: "Summit Bank",
    entityId: "entity-standalone",
    entityName: "Standalone Holdings LP",
    collectionId: null,
    collectionName: null,
    balance: usd(76_250_00),
    delta: usd(3_400_00),
    deltaPercent: 4.67,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-5",
    bankAccountLast4: "3002",
    bankName: "First National",
    entityId: "entity-standalone",
    entityName: "Standalone Holdings LP",
    collectionId: null,
    collectionName: null,
    balance: usd(12_000_00),
    delta: usd(-800_00),
    deltaPercent: -6.25,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-1",
    bankAccountLast4: "1001",
    bankName: "First National",
    entityId: "entity-evg-1",
    entityName: "Evergreen Fund I",
    collectionId: "col-evg",
    collectionName: "Evergreen Fund Family",
    balance: usd(125_000_00),
    delta: usd(5_000_00),
    deltaPercent: 4.17,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-2",
    bankAccountLast4: "1002",
    bankName: "Summit Bank",
    entityId: "entity-evg-1",
    entityName: "Evergreen Fund I",
    collectionId: "col-evg",
    collectionName: "Evergreen Fund Family",
    balance: usd(48_500_00),
    delta: usd(-1_250_00),
    deltaPercent: -2.51,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-6",
    bankAccountLast4: "4001",
    bankName: "Summit Bank",
    entityId: "entity-hz-1",
    entityName: "Horizon Ventures I",
    collectionId: "col-hz",
    collectionName: "Horizon Capital Group",
    balance: usd(215_000_00),
    delta: usd(9_750_00),
    deltaPercent: 4.75,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-7",
    bankAccountLast4: "4002",
    bankName: "Pacific Trust",
    entityId: "entity-hz-1",
    entityName: "Horizon Ventures I",
    collectionId: "col-hz",
    collectionName: "Horizon Capital Group",
    balance: usd(0),
    delta: usd(0),
    deltaPercent: 0,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-8",
    bankAccountLast4: "4003",
    bankName: "First National",
    entityId: "entity-hz-2",
    entityName: "Horizon Ventures II",
    collectionId: "col-hz",
    collectionName: "Horizon Capital Group",
    balance: usd(560_000_00),
    delta: usd(-14_200_00),
    deltaPercent: -2.47,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-9",
    bankAccountLast4: "3003",
    bankName: "Pacific Trust",
    entityId: "entity-standalone",
    entityName: "Standalone Holdings LP",
    collectionId: null,
    collectionName: null,
    balance: usd(198_750_00),
    delta: usd(12_500_00),
    deltaPercent: 6.71,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-10",
    bankAccountLast4: "5001",
    bankName: "Summit Bank",
    entityId: "entity-solo-2",
    entityName: "Cedar Point Partners",
    collectionId: null,
    collectionName: null,
    balance: usd(34_900_00),
    delta: usd(-2_100_00),
    deltaPercent: -5.68,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  // Collection "Meridian Holdings" — entities with deliberately dissimilar names
  // (not the "Fund I/II" pattern) so grouping/sorting is exercised on varied text.
  {
    bankAccountId: "acct-11",
    bankAccountLast4: "6001",
    bankName: "First National",
    entityId: "entity-mer-1",
    entityName: "Blue Harbor Real Estate Trust",
    collectionId: "col-mer",
    collectionName: "Meridian Holdings",
    balance: usd(1_250_000_00),
    delta: usd(48_000_00),
    deltaPercent: 3.99,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-12",
    bankAccountLast4: "6002",
    bankName: "Pacific Trust",
    entityId: "entity-mer-2",
    entityName: "Ironwood Infrastructure Partners",
    collectionId: "col-mer",
    collectionName: "Meridian Holdings",
    balance: usd(87_300_00),
    delta: usd(-5_600_00),
    deltaPercent: -6.03,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-13",
    bankAccountLast4: "6003",
    bankName: "Summit Bank",
    entityId: "entity-mer-3",
    entityName: "Zenith Growth Opportunities Fund",
    collectionId: "col-mer",
    collectionName: "Meridian Holdings",
    balance: usd(402_150_00),
    delta: usd(15_900_00),
    deltaPercent: 4.12,
    currencyCode: "USD",
    asOf: AS_OF,
  },
  {
    bankAccountId: "acct-14",
    bankAccountLast4: "6004",
    bankName: "First National",
    entityId: "entity-mer-3",
    entityName: "Zenith Growth Opportunities Fund",
    collectionId: "col-mer",
    collectionName: "Meridian Holdings",
    balance: usd(59_875_00),
    delta: usd(2_310_00),
    deltaPercent: 4.01,
    currencyCode: "USD",
    asOf: AS_OF,
  },
];
